import type { Tour } from '../tour-state.js';

// Journey A: one step per stage card, each moving on when the stage really changes. The card is the
// highlighted target, so the user can answer the question; a click on its Next hides the bubble
// until the stage moves on (a failed Next shows its error and leaves the bubble hidden).
const card = (stage: string) => `onboarding-${stage}`;
const next = (stage: string) => `onboarding-${stage}-next`;

const onboarding: Tour = {
  id: 'onboarding',
  steps: [
    {
      id: 'first-name',
      stage: 'FIRST_NAME_STAGE',
      targets: [card('FIRST_NAME_STAGE')],
      hideOn: [next('FIRST_NAME_STAGE')],
      text: 'Welcome to nodeStream! Check or type your first name, then press Next.',
      advance: 'action',
      doneOn: ['stage'],
    },
    {
      // The last-name card is the first with a finished step, so it's where the dots can be shown
      // working: only finished steps and the current one can be clicked
      id: 'step-dots',
      stage: 'LAST_NAME_STAGE',
      targets: ['onboarding-steps', card('LAST_NAME_STAGE')],
      // Beside the dots it would cover the card's field; above them, lined up with their left edge
      placement: 'top-start',
      hideOn: [next('LAST_NAME_STAGE')],
      text: 'These dots show your progress. Click a finished step to go back to it.',
      advance: 'action',
      doneOn: ['stage'],
    },
    {
      id: 'back-next',
      stage: 'JOB_TITLE_STAGE',
      targets: [card('JOB_TITLE_STAGE')],
      anchor: next('JOB_TITLE_STAGE'),
      hideOn: [next('JOB_TITLE_STAGE')],
      text: 'Back returns to the previous question and Next moves on. You can change any answer until you confirm at the end.',
      advance: 'action',
      doneOn: ['stage'],
    },
    {
      id: 'director',
      stage: 'DIRECTOR_USER',
      targets: [card('DIRECTOR_USER')],
      hideOn: [next('DIRECTOR_USER')],
      text: "Say yes if you're a director or officer of COMPANY_NAME. You'll then verify and validate the company with the official government registrar, the taxation authority and your bank.",
      advance: 'action',
      doneOn: ['stage'],
      when: (context) => !context.enrolledCompany,
    },
    {
      // An invitee joining an enrolled company: the company's verified director confirms the claim
      // (ns-lambda-ts company/create-after-invitation-verification-workflows.ts), and the payment
      // stage is left out (view-onboarding.ts)
      id: 'director-enrolled',
      stage: 'DIRECTOR_USER',
      targets: [card('DIRECTOR_USER')],
      hideOn: [next('DIRECTOR_USER')],
      text: "Say yes if you're a director or officer of COMPANY_NAME, and the company's director will confirm it. COMPANY_NAME is already on nodeStream, so you won't be asked about payments.",
      advance: 'action',
      doneOn: ['stage'],
      when: (context) => !!context.enrolledCompany,
    },
    {
      id: 'payment',
      stage: 'AUTHROISED_USER',
      targets: [card('AUTHROISED_USER')],
      hideOn: [next('AUTHROISED_USER')],
      text: "Say yes if you're authorised to make payments for COMPANY_NAME. You'll then set up the company's nodeStream subscription.",
      advance: 'action',
      doneOn: ['stage'],
      when: (context) => !context.enrolledCompany,
    },
    {
      // The dots had their own step, so only the card is highlighted here, and its Back goes back
      id: 'review',
      stage: 'REVIEW',
      targets: [card('REVIEW')],
      hideOn: [next('REVIEW')],
      text: "Check your answers, and press Back to change one. When they're right, tick the box and press Confirm.",
      advance: 'action',
      doneOn: ['onboarding-complete'],
    },
  ],
};

export default onboarding;
